import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ChefSubscription = ({ user }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get('/subscription/chef/status');
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    // Check for SafeServ certification first
    try {
      const profileResponse = await axios.get(`/chef/profile/${user.id}`);
      const profile = profileResponse.data;
      
      // Skip checks for master admin test accounts
      if (profile.master_admin_test_account) {
        alert('✅ Master admin test account - subscription bypassed!');
        return;
      }
      
      if (!profile.safeserv_cert_number || profile.safeserv_cert_number === 'MASTER_BYPASS') {
        alert('❌ You must provide your SafeServ certification number before subscribing. Please complete your profile.');
        return;
      }
      
      if (!profile.safeserv_cert_url) {
        alert('⚠️ Please upload your SafeServ certification document in your profile before subscribing.');
        return;
      }
      
      if (!profile.safeserv_verified) {
        alert('⏳ Your SafeServ certification is pending admin verification. You can subscribe once verified.');
        return;
      }
    } catch (err) {
      alert('Unable to verify SafeServ certification status');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await axios.post('/subscription/chef/subscribe');
      alert('✅ ' + response.data.message);
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Subscription failed');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }
    
    try {
      await axios.post('/subscription/chef/cancel');
      alert('✅ Subscription cancelled');
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Cancellation failed');
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-600">Loading subscription...</div>;
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6" data-testid="chef-subscription">
      {status?.subscribed ? (
        // Active Subscription
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="text-3xl">✅</div>
            <div>
              <h3 className="text-xl font-bold text-green-800">Active Subscription</h3>
              <p className="text-sm text-green-700">You're subscribed at $79.99/month</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-600 mb-2">
              <strong>Status:</strong> <span className="text-green-600">Active ✓</span>
            </div>
            <div className="text-sm text-gray-600">
              <strong>Started:</strong> {new Date(status.started_at).toLocaleDateString()}
            </div>
          </div>

          <p className="text-sm text-gray-700 mb-4">
            Your subscription is active and you can accept bookings from customers.
          </p>

          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            data-testid="cancel-subscription-button"
          >
            Cancel Subscription
          </button>
        </div>
      ) : (
        // No Subscription
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="text-3xl">💳</div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Chef Platform Subscription</h3>
              <p className="text-lg font-semibold text-green-600">$79.99/month</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 mb-4">
            <h4 className="font-semibold mb-2">What's Included:</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✅ Accept unlimited bookings</li>
              <li>✅ Create your chef profile</li>
              <li>✅ Upload photos & videos</li>
              <li>✅ Receive customer payments</li>
              <li>✅ Virtual grocery cards for each booking</li>
              <li>✅ Full platform access</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Required to accept bookings:</strong> You must subscribe to become an active chef on the platform.
            </p>
          </div>

          <button
            onClick={handleSubscribe}
            disabled={subscribing}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="subscribe-button"
          >
            {subscribing ? 'Processing...' : 'Subscribe Now - $79.99/month'}
          </button>

          <p className="text-xs text-gray-500 mt-3 text-center">
            Secure payment via Stripe • Cancel anytime
          </p>
        </div>
      )}
    </div>
  );
};

export default ChefSubscription;
